import { isLinkedReadingRoute } from "@/domain/split_routes";
import { useFamilyData } from "@/hooks/use_family_data";
import { FamilyScope } from "@/features/horizon/family_scope";
/**
 * The app shell: auth → web install gate → plan gate → the app.
 *
 * Accounts without an entitlement choose a plan after the web install gate.
 *
 * **`/` is the numpad** (SPEC.md §3). The front door is a route, not a
 * preference: the manifest's `start_url`, the Android shortcut, an iOS Shortcut,
 * a bookmark and a shared link all land on capture, because they all land on the
 * root. Horizon — the waterline — is `/home`, one tap away and never in the way.
 * `/add` stays as a redirect so every existing bookmark and shortcut still work.
 *
 * Below the routes sits the tab bar (`tab_bar.tsx`), the second thing this shell
 * draws outside a layout. Its height is measured into `--shell-tabs` for the
 * same reason the session band's is measured into `--shell-band`.
 */

import { useTabSwipe } from "@/hooks/use_tab_swipe";
import { markRouteLoaded, routeCommitted } from "@/lib/route_transition";
import { salaryInForce } from "@/domain/salary";
import { storeListing } from "@/native/store_listing";
import { useProtocolUpdate } from "@/hooks/use_protocol_update";
import { Suspense, lazy, useLayoutEffect, useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useNativeUploadStatus } from "@/hooks/use_native_upload_status";
import { usePlanGate } from "@/hooks/use_plan_gate";
import { listenForPurchases } from "@/native/purchase_runtime";
import { bridge } from "@/native/impl";
import { useLapse } from "@/hooks/use_lapse";
import { CaptureLayout } from "@/features/capture/capture_layout";
import { SessionBand } from "@/features/common/session_band";
import { SIDEBAR_PAD, TabBar, bottomBarHiddenOn } from "@/features/common/tab_bar";
import { HorizonView } from "@/features/horizon/horizon_view";
import { InstallGateView, SalaryStepView, SignInView } from "@/features/onboarding/onboarding_view";
import { NewAccountSheet } from "@/features/onboarding/leave_sheets";
import { ReauthSheet } from "@/features/onboarding/reauth_sheet";
import { ConfirmSheet } from "@/features/common/confirm_sheet";
import { useNativeThreshold } from "@/hooks/use_native_threshold";
import { useNativeNotifications } from "@/hooks/use_native_notifications";
import { useNativeSnapshot } from "@/hooks/use_native_snapshot";
import { useAuth } from "@/hooks/use_auth";
import {
  useCurrentPeriod,
  usePendingCount,
  useProfile,
  useSalaries,
} from "@/hooks/use_data";
import { useInstallState } from "@/hooks/use_install";
import { useNativeShell } from "@/hooks/use_native_shell";
import { seedDefaultCategoriesIfEmpty, updateProfile } from "@/lib/repo";
import {
  firstPullSettled,
  shouldSeedDefaults,
  startSyncTriggers,
  subscribeSync,
} from "@/lib/sync";

/**
 * Reserve exactly the space the band actually occupies.
 *
 * Every route root is a `min-h-dvh` column, so anything the shell draws above
 * one makes that screen taller than the viewport and gives it a scrollbar unless
 * the same amount is subtracted back (`--shell-band`, `index.css`).
 *
 * It is **measured** rather than declared as a constant, because a constant is a
 * second source of truth for a height the band decides for itself. It grows with
 * the root font size (the copy wraps to two lines long before the viewport is
 * narrow) and with `env(safe-area-inset-top)` on a notched device — neither of
 * which a hard-coded `rem` value tracks, and either of which reintroduces the
 * scroll it was written to prevent.
 */
function useMeasuredHeight(): [(node: HTMLElement | null) => void, number] {
  const [height, setHeight] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  // A **callback ref**, not an effect. The measured element is mounted
  // conditionally, so an effect would need the mount condition in its
  // dependencies to notice the node appearing — and an effect with no
  // dependency array (the other way to catch it) would tear down and rebuild the
  // observer on every render of the shell, forcing a synchronous layout read
  // each time. React calls this exactly when the node attaches and detaches.
  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;

    if (!node) {
      setHeight(0);
      return;
    }
    const measure = (): void => {
      setHeight(node.offsetHeight);
    };
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(node);
  }, []);

  return [ref, height];
}

// Horizon and Capture are the two screens on the critical path — the app opens
// on one of them. Ledger and Settings are visited deliberately, so they are
// fetched on demand rather than sitting in the bundle that gates first paint.
const LedgerView = lazy(() =>
  import("@/features/ledger/ledger_view").then((m) => (markRouteLoaded("/ledger"), { default: m.LedgerView })),
);
import { FamilyRemovedNotice, FamilyHomeNotices } from "@/features/settings/family_removed";
const SplitsView = lazy(() => import("@/features/splits/splits_view").then(m => ({ default: m.SplitsView })));
const SplitGroupView = lazy(() => import("@/features/splits/split_group_view").then(m => ({ default: m.SplitGroupView })));
const FamilyCategoriesView = lazy(() => import("@/features/settings/family_categories").then(m => ({ default: m.FamilyCategoriesView })));
const FamilySettingsView = lazy(() => import("@/features/settings/family_settings").then(m => ({ default: m.FamilySettingsView })));
const JoinSplitView = lazy(() => import("@/features/splits/join_split").then(m => ({ default: m.JoinSplitView })));
const LinkedSplitView = lazy(() => import("@/features/splits/linked_split").then(m => ({ default: m.LinkedSplitView })));
const JoinFamilyView = lazy(() => import("@/features/settings/join_family").then(m => ({ default: m.JoinFamilyView })));
const AdminView = lazy(() => import("@/features/admin/admin_view").then((m) => ({ default: m.AdminView })));
const SettingsView = lazy(() =>
  import("@/features/settings/settings_view").then((m) => (markRouteLoaded("/settings"), { default: m.SettingsView })),
);
const BudgetSettingsView = lazy(() =>
  import("@/features/settings/budget_settings").then((m) => ({ default: m.BudgetSettingsView })),
);
const AnalyticsView = lazy(() =>
  import("@/features/analytics/analytics_view").then((m) => (markRouteLoaded("/analytics"), { default: m.AnalyticsView })),
);
const CategorySettingsView = lazy(() => import("@/features/settings/category_settings").then(m => ({ default: m.CategorySettingsView })));
const SavingsView = lazy(() => import("@/features/savings/savings_view").then((m) => ({ default: m.SavingsView })));
const InsightDetailView = lazy(() =>
  import("@/features/analytics/insights/insight_detail_view").then((m) => ({ default: m.InsightDetailView })),
);
// Sounding is reached deliberately, from a chip, and pulls in nothing the rest
// of the app needs — so it is fetched on demand rather than sitting in the
// bundle that gates first paint.
const SoundingView = lazy(() =>
  import("@/features/sounding/sounding_view").then((m) => ({ default: m.SoundingView })),
);
const SoundingHistory = lazy(() =>
  import("@/features/sounding/history_view").then((m) => ({ default: m.SoundingHistory })),
);

const PlanView = lazy(() => import("@/features/settings/plan_view").then((m) => ({ default: m.PlanView })));
const PaywallView = lazy(() => import("@/features/onboarding/paywall").then((m) => ({ default: m.PaywallView })));

export function App(): React.JSX.Element {
  const lapse = useLapse();
  const nativeUploads = useNativeUploadStatus();
  const { state, userId, reauthOpen, requestReauth, closeReauth, newAccountNotice, setNewAccountNotice } =
    useAuth();
  const planGate = usePlanGate(state === "signed-in" ? userId : null);
  useEffect(() => {
    if (state === "signed-in") return listenForPurchases();
  }, [state]);

  const updateRequired = useProtocolUpdate();
  const storeUrl = storeListing(bridge.platform);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const profile = useProfile();
  const familyData = useFamilyData();
  const install = useInstallState();
  const pending = usePendingCount();
  const location = useLocation();
  useTabSwipe();
  useLayoutEffect(() => { routeCommitted(location.pathname); }, [location]);
  const [bandRef, bandHeight] = useMeasuredHeight();
  const [tabsRef, tabsHeight] = useMeasuredHeight();
  useNativeShell();
  useNativeSnapshot(userId);
  const notifications = useNativeNotifications(userId, planGate.entitlement?.state === "trial" ? planGate.entitlement.periodEnd : null);
  const nativeMark = useNativeThreshold();
  const { rows: salaries, loaded: salariesLoaded } = useSalaries();
  const { period } = useCurrentPeriod();
  const [pullSettled, setPullSettled] = useState(false);

  // Sync starts as soon as there is a session.
  useEffect(() => {
    if (state !== "signed-in") return;
    return startSyncTriggers();
  }, [state]);

  // Seed the eight defaults for a brand-new account — but only once a pull has
  // actually completed, so an existing set has had its chance to arrive.
  // `shouldSeedDefaults` owns that condition and is unit-tested; it is subtler
  // than it looks (F2).
  useEffect(() => {
    if (state !== "signed-in") return;
    return subscribeSync((s) => {
      if (!shouldSeedDefaults(s)) return;
      void seedDefaultCategoriesIfEmpty();
    });
  }, [state]);

  // The same condition, for the salary gate below: it must not conclude "this
  // account has no salary" from a Dexie the first pull has not filled yet.
  // Tracked, **not latched**. A latch survives `resetSyncStatus()`, which is
  // exactly what an in-tab account switch depends on: sign out and back in and
  // neither path reloads, so this component is never unmounted. A stuck `true`
  // would let the gate conclude the incoming account has no salary before its
  // first pull — and the figure typed there wins on `effectiveFrom` over the
  // real salary arriving a moment later.
  //
  // Following the predicate down as well as up costs nothing: a `false` only
  // ever hides the gate, which is the safe direction.
  useEffect(() => {
    if (state === "signed-out") return;
    return subscribeSync((s) => {
      setPullSettled(firstPullSettled(s));
    });
  }, [state]);

  // Reaching the app from an installed shell completes onboarding.
  //
  // The device timezone is captured here, not left to the schema default. The
  // column defaults to Asia/Singapore, and every period boundary is computed in
  // the profile's zone — so without this a London user's day would roll over at
  // 17:00 their time, and an expense logged in the evening would land in the
  // wrong period with nothing to indicate why.
  useEffect(() => {
    if (state === "signed-in" && install.isInstalled && profile.loaded && !profile.onboardedAt) {
      const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      void updateProfile({
        onboardedAt: new Date().toISOString(),
        ...(deviceZone ? { timezone: deviceZone } : {}),
      });
    }
  }, [state, install.isInstalled, profile.loaded, profile.onboardedAt]);

  // Mirrored onto the root as well: toasts and sheets are portalled to <body>,
  // outside this element, and would otherwise read the 0px fallback and sit on
  // top of the tab bar.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--shell-band", `${String(bandHeight)}px`);
    root.setProperty("--shell-tabs", `${String(tabsHeight)}px`);
  }, [bandHeight, tabsHeight]);

  if (state === "loading") return <Splash />;
  if (state === "signed-out") return <SignInView familyInvite={/^\/join\/(?:s(?:\/[^/]+)?|[^/]+)$/.test(location.pathname)} />;

  if (/^\/join\/(?:s(?:\/[^/]+)?|[^/]+)$/.test(location.pathname)) return <Suspense fallback={<Splash />}><Routes><Route path="/join/s" element={<JoinSplitView />} /><Route path="/join/s/:token" element={<JoinSplitView />} /><Route path="/join/:token" element={<JoinFamilyView />} /></Routes>{reauthOpen ? <ReauthSheet onClose={closeReauth} /> : null}</Suspense>;

  const linkedReading = isLinkedReadingRoute(location.pathname);

  // Desktop browsers vary in install support and the 7-day eviction is an iOS
  // Safari behaviour, so desktop may proceed once it has acknowledged the gate.
  const gated =
    bridge.platform === "web" && !install.isInstalled && (install.platform !== "desktop" || profile.onboardedAt === null);
  if (gated && !linkedReading) return <InstallGateView />;
  if (state === "signed-in" && planGate.loading) return <Splash />;

  if (state === "signed-in" && planGate.needsPlan && !linkedReading) return <Suspense fallback={<Splash />}><PaywallView /></Suspense>;

  // The salary gate — after the install gate, never before. On iOS the
  // pre-install tab lives in storage Safari evicts after seven idle days, so a
  // figure entered there could be gone before it was ever pushed.
  //
  // Gated on the row's absence rather than a "have we asked?" flag, and only
  // once the first pull has settled: on a second device the salary arrives by
  // sync, and asking again would overwrite it with a fresh `effectiveFrom`
  // version. Existing accounts meet this once, on the first load after the
  // upgrade, which is the cost of the figure being mandatory.
  // `salariesLoaded` is load-bearing, not belt-and-braces. Offline,
  // `firstPullSettled` is true immediately and `subscribeSync` replays the
  // current status synchronously — so without it the gate races the Dexie read
  // and wins, putting a full-screen form in front of the numpad on a cold
  // airplane-mode boot for an account that already has a salary (INV-4). It
  // would also then write a *second* version at the current period start, which
  // is the overwrite the pull-settled check exists to avoid.
  if (!linkedReading && salariesLoaded && pullSettled && salaryInForce(salaries, period, profile.timezone) === null) {
    return <SalaryStepView />;
  }

  // `"session-expired"` falls through to the app on purpose. The token is dead,
  // but the ledger is not, and every read below is Dexie — so the only thing
  // actually broken is syncing, and that is what the band says (§S.1).
  //
  // One condition, used for the band, the height reservation and the safe-area
  // handover alike. Encoding it more than once is how the reservation drifts out
  // of step with what is on screen.
  const showSessionBand = state === "session-expired" && pending > 0;
  const showBand = showSessionBand || lapse.captureBlocked || planGate.error !== null || nativeUploads.held + nativeUploads.failed > 0;
  const hideBottomBar = bottomBarHiddenOn(location.pathname);

  return (
    // Custom properties, which `CSSProperties` has no index signature for.
    <div
      style={
        {
          "--shell-band": `${String(bandHeight)}px`,
          "--shell-tabs": `${String(tabsHeight)}px`,
        } as React.CSSProperties
      }
    >

      {updateRequired && !updateDismissed ? (
        <ConfirmSheet
          openHaptic={false}
          title="Update required"
          body="Update Tide Tally before syncing. Your unsynced changes remain on this device."
          actions={bridge.platform === "web"
            ? [{ label: "Reload", onSelect: () => window.location.reload() }]
            : storeUrl !== null
              ? [{ label: "Open store", onSelect: () => { window.location.href = storeUrl; } }]
              : []}
          cancelLabel="Keep reading offline"
          onCancel={() => setUpdateDismissed(true)}
        />
      ) : null}
      {nativeMark !== null ? <p role="status" className="fixed inset-x-0 bottom-24 z-50 bg-bg-2 px-phi-1 py-phi-n1 text-center">Budget reached {nativeMark}%</p> : null}
      {notifications.explain ? <ConfirmSheet openHaptic={false} title="Allow reminders?"
        body="Tide Tally can remind you when repeating expenses are due and your trial is ending. Expenses are only added when you confirm them."
        actions={[{ label: "Continue", onSelect: notifications.accept }]}
        cancelLabel="Not now" onCancel={notifications.dismiss} /> : null}
      {/* Mounted only when the band actually shows — which is almost never.
          An always-mounted wrapper would still carry `pt-safe`, so on a notched
          device it would be an empty strip the height of the inset: every route
          root permanently shortened by it, and the handover below permanently
          on. Horizon's water is `absolute inset-0` inside such a root, so it
          would stop short of the top of the screen for every iOS user, band or
          no band. */}
      {showBand ? (
        // Outside the route wrapper, so the band keeps the real top inset.
        <div ref={bandRef} className="feel-shell-band pt-safe">
          {nativeUploads.held + nativeUploads.failed > 0 ? <p role="status" className="border-b border-line bg-acc-soft px-phi-1 py-phi-n2 text-phi-n1">{nativeUploads.held} changes waiting for a plan · {nativeUploads.failed} changes could not upload. Saved on this device; open Settings or contact support.</p> : null}
          {planGate.error ? <p role="status" className="border-b border-line bg-acc-soft px-phi-1 py-phi-n2 text-phi-n1">{planGate.error}</p> : null}
          {showSessionBand ? <SessionBand
            pending={pending}
            onSignIn={requestReauth}
          /> : null}
          {lapse.captureBlocked ? <Link to="/settings/plan" className="block min-h-[44px] content-center border-b border-line bg-acc-soft px-phi-1 py-phi-n1 text-phi-n1 text-acc">Your plan has ended. {familyData?.family ? "New expenses aren't saved to the family or to you. " : ""}Resubscribe{lapse.held > 0 ? ` · ${lapse.held} changes saved here` : " to keep capturing"}</Link> : null}
        </div>
      ) : null}

      {/* While the band is up it owns the top safe area, so the route roots
          underneath must not apply their own `pt-safe` on top — that would leave
          a second, empty inset the height of the notch above the numpad.

          `SIDEBAR_PAD` is the desktop half of the same idea: above `md` the
          navigation is a fixed left rail, and the routes are inset by exactly
          its width. On a phone it resolves to nothing, and it applies on every
          route — the rail is drawn everywhere, including `/ask`, where only the
          phone's bottom bar stands down for the dock. */}
      <div
        data-shell-routes
        className={`${SIDEBAR_PAD} pb-shell-tabs`}
        style={showBand ? ({ "--shell-safe-top": "0px" } as React.CSSProperties) : undefined}
      >
        <Routes>
          {/* The numpad is the root. Everything that opens this app — the
              manifest's `start_url`, the Android shortcut, an iOS Shortcut, a
              bookmark, a link somebody sent — resolves here, so there is no
              path in which the front door is a dashboard. */}
          <Route path="/" element={<CaptureLayout />} />
          <Route path="/splits/linked/:linkId" element={<Suspense fallback={<Splash />}><LinkedSplitView /></Suspense>} />
          <Route path="/splits" element={<Suspense fallback={<Splash />}><SplitsView /></Suspense>} />
          <Route path="/splits/:groupId" element={<Suspense fallback={<Splash />}><SplitGroupView /></Suspense>} />
          <Route path="/settings/plan" element={<Suspense fallback={<Splash />}><PlanView /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={<Splash />}><AdminView /></Suspense>} />
          <Route path="/home" element={<HorizonView />} />
          <Route path="/family" element={<FamilyScope><HorizonView periodEndMoments={false} /></FamilyScope>} />
          {/* Every install, shortcut and test that predates the move. A redirect
              rather than a second component: two routes rendering Capture would
              be two places for the active-tab state to disagree. */}
          <Route path="/add" element={<Navigate to="/" replace />} />
          <Route
            path="/ledger"
            element={
              <Suspense fallback={<Splash />}>
                <LedgerView />
              </Suspense>
            }
          />
          {/* Reached from a line on Horizon, not a tab — the bar stays at five
              (DESIGN_SYSTEM.md §4.7), and lazy because savings are consulted
              occasionally while the numpad is opened dozens of times a day. */}
          <Route
            path="/savings"
            element={
              <Suspense fallback={<Splash />}>
                <SavingsView />
              </Suspense>
            }
          />
          <Route
            path="/analytics"
            element={
              <Suspense fallback={<Splash />}>
                <AnalyticsView />
              </Suspense>
            }
          />
          <Route path="/analytics/insights/:id" element={<Suspense fallback={<Splash />}><InsightDetailView /></Suspense>} />
          <Route
            path="/ask"
            element={
              <Suspense fallback={<Splash />}>
                <SoundingView />
              </Suspense>
            }
          />
          {/* A route rather than a `?history=` sub-screen, so the OS back gesture
              and a bookmark both work on it. It keeps the tab bar, because
              `bottomBarHiddenOn` matches `/ask` exactly — and that is right: a
              list has no dock to compete with, so from here you can go straight
              to any tab. */}
          <Route
            path="/ask/history"
            element={
              <Suspense fallback={<Splash />}>
                <SoundingHistory />
              </Suspense>
            }
          />
          <Route
            path="/settings"
            element={
              <Suspense fallback={<Splash />}>
                <SettingsView />
              </Suspense>
            }
          />
          {/* A section of Settings by URL — `/settings/agents` is what the
              device-pairing flow hands people, and the catch-all below used
              to send it to the numpad. `SettingsView` scrolls to the slug. */}
          <Route
            path="/settings/:section"
            element={
              <Suspense fallback={<Splash />}>
                <SettingsView />
              </Suspense>
            }
          />
          <Route path="/settings/categories/:categoryId" element={<Suspense fallback={<Splash />}><CategorySettingsView /></Suspense>} />
          <Route path="/settings/family/budget" element={<Suspense fallback={<Splash />}><FamilyScope><BudgetSettingsView /></FamilyScope></Suspense>} />
          <Route path="/settings/family/categories" element={<Suspense fallback={<Splash />}><FamilyScope><FamilyCategoriesView /></FamilyScope></Suspense>} />
          <Route path="/settings/family/categories/:categoryId" element={<Suspense fallback={<Splash />}><FamilyScope><CategorySettingsView /></FamilyScope></Suspense>} />
          <Route path="/settings/family" element={<Suspense fallback={<Splash />}><FamilySettingsView /></Suspense>} />
          <Route path="/settings/budget" element={<Suspense fallback={<Splash />}><BudgetSettingsView /></Suspense>} />
          <Route path="/settings/month" element={<Navigate to="/settings/budget" replace />} />
          <Route path="/settings/rollover" element={<Navigate to="/settings/budget" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {location.pathname === "/home" ? <><FamilyHomeNotices /><FamilyRemovedNotice /></> : null}
      <TabBar barRef={tabsRef} hideBottom={hideBottomBar} />

      {reauthOpen ? (
        <ReauthSheet onClose={closeReauth} />
      ) : null}

      {newAccountNotice && state === "signed-in" ? (
        <NewAccountSheet
          onDismiss={() => {
            setNewAccountNotice(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Splash(): React.JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <svg
        viewBox="0 0 26 52"
        aria-label="Tide Tally"
        className="h-12 w-6 animate-pulse stroke-acc"
        fill="none"
        strokeWidth="1.4"
      >
        <path d="M13 52V14" />
        <path d="M3 4v10a10 10 0 0 0 20 0V4" />
        <path d="M13 14V2" />
        <path d="M3 4 1 8M23 4l2 4" />
        <path d="M7 44h12" />
      </svg>
    </div>
  );
}
